// src/store/modules/tagsView.js
import path from 'path-browserify'
import router from '@/router' // 引入路由实例

// 状态管理 - 存储已访问的视图和缓存的视图
const state = {
  visitedViews: [], // 已访问的页面标签列表
  cachedViews: []   // 需要缓存的组件名称列表
}

// 状态变更操作 - 同步修改state的方法
const mutations = {
  // 添加已访问的视图标签
  ADD_VISITED_VIEW: (state, view) => {
    // 只检查路径是否相同（忽略查询参数）
    const exists = state.visitedViews.some(v => v.path === view.path);
    // 如果不存在相同视图，则添加到访问记录中
    if (!exists) {
      state.visitedViews.push({
        fullPath: view.fullPath || view.path, // 完整路径，如果不存在则使用path
        path: view.path,                      // 路径
        name: view.name,                      // 路由名称
        meta: { ...view.meta },               // 路由元信息副本
        query: view.query || {},              // 查询参数，默认为空对象
        title: view.meta?.title || view.name || '未命名' // 标题，优先使用meta中的title
      })
    }
  },
  // 添加缓存视图
  ADD_CACHED_VIEW: (state, view) => {
    // 只有当视图有名称且未被缓存时才添加
    if (view.name && !state.cachedViews.includes(view.name)) {
      // 如果该视图需要缓存（meta.cache不为false），则添加到缓存列表
      if (view.meta?.cache !== false) {
        state.cachedViews.push(view.name)
      }
    }
  },

  // 删除已访问的视图标签
  DEL_VISITED_VIEW: (state, view) => {
    // 只根据路径删除（忽略查询参数）
    state.visitedViews = state.visitedViews.filter(v => v.path !== view.path);
  },

  // 删除缓存视图
  DEL_CACHED_VIEW: (state, view) => {
    if (view.name) {
      // 检查是否允许手动关闭时清除缓存
      const allowManualUncache = view.meta?.allowManualUncache !== false
      if (allowManualUncache) {
        state.cachedViews = state.cachedViews.filter(name => name !== view.name)
      }
    }
  },

  // 删除其他已访问的视图标签（只保留当前视图和固定标签）
  DEL_OTHERS_VISITED_VIEWS: (state, view) => {
    // 保留固定标签和当前视图，删除其他所有视图
    state.visitedViews = state.visitedViews.filter(
        v => v.meta?.affix || // 保留固定标签
            (v.path === view.path && // 或者保留当前视图（路径和查询参数都匹配）
                JSON.stringify(v.query) === JSON.stringify(view.query))
    )
  },

  // 删除其他缓存视图（只保留当前视图）
  DEL_OTHERS_CACHED_VIEWS: (state, view) => {
    // 如果当前视图为固定标签，则保留其缓存，否则清空缓存
    state.cachedViews = view.meta?.affix ? [view.name] : []
  },

  // 删除所有已访问的视图标签（保留固定标签）
  DEL_ALL_VISITED_VIEWS: (state) => {
    // 只保留固定标签，删除其他所有访问记录
    state.visitedViews = state.visitedViews.filter(tag => tag.meta?.affix)
  },

  // 删除所有缓存视图
  DEL_ALL_CACHED_VIEWS: (state) => {
    // 清空缓存列表
    state.cachedViews = []
  },

  // 更新已访问的视图信息
  UPDATE_VISITED_VIEW: (state, view) => {
    // 只根据路径查找（忽略查询参数）
    const index = state.visitedViews.findIndex(v => v.path === view.path);

    if (index !== -1) {
      // 更新时保留原有查询参数，除非明确传递新参数
      const newView = {
        ...state.visitedViews[index],
        ...view
      };

      // 确保路径不变
      newView.path = view.path;

      state.visitedViews.splice(index, 1, newView);
    } else {
      // 如果没找到，添加新的视图记录
      state.visitedViews.push({
        fullPath: view.fullPath || view.path,
        path: view.path,
        name: view.name,
        meta: { ...view.meta },
        query: view.query || {},
        title: view.meta?.title || view.name || '未命名'
      })
    }
  }
}

// 异步操作 - 通过dispatch调用，可以包含异步操作
const actions = {
  // 添加视图（包括访问记录和缓存）
  addView ({ dispatch, state }, view) {

    // 跳过隐藏路由（meta.hidden为true的路由不添加标签）
    if (view.meta?.hidden) {
      return
    }
    if(state.visitedViews.find(item => item.path === view.path)){
      return
    }
    // 添加访问记录
    dispatch('addVisitedView', view)

    // 添加缓存（只有具名路由才缓存）
    if (view.name) {
      dispatch('addCachedView', view)
    }
  },

  // 添加访问记录
  addVisitedView ({ commit }, view) {

    commit('ADD_VISITED_VIEW', view)
  },

  // 添加组件缓存
  addCachedView ({ commit }, view) {
    commit('ADD_CACHED_VIEW', view)
  },

  // 删除视图（同时删除访问记录和缓存）
  delView ({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delVisitedView', view)
      // 强制删除缓存，即使用户设置了 cache: true
      dispatch('delCachedView', view)
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },

  // 删除访问记录
  delVisitedView ({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_VISITED_VIEW', view)
      resolve([...state.visitedViews])
    })
  },

  // 删除组件缓存
  delCachedView ({ commit, state }, view) {
    return new Promise(resolve => {
      // 即使用户设置了 cache: true，手动关闭时也要清除缓存
      if (view.name) {
        commit('DEL_CACHED_VIEW', view)
      }
      resolve([...state.cachedViews])
    })
  },

  // 删除其他视图（关闭其他标签页）
  delOthersViews ({ dispatch }, view) {
    return new Promise(resolve => {
      dispatch('delOthersVisitedViews', view)
      dispatch('delOthersCachedViews', view)
      resolve()
    })
  },

  // 删除其他访问记录
  delOthersVisitedViews ({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_OTHERS_VISITED_VIEWS', view)
      resolve([...state.visitedViews])
    })
  },

  // 删除其他组件缓存
  delOthersCachedViews ({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_OTHERS_CACHED_VIEWS', view)
      resolve([...state.cachedViews])
    })
  },

  // 删除所有视图（关闭所有标签页）
  delAllViews ({ dispatch, state }) {
    return new Promise(resolve => {
      dispatch('delAllVisitedViews')
      dispatch('delAllCachedViews')
      resolve({
        visitedViews: [...state.visitedViews]
      })
    })
  },

  // 删除所有访问记录
  delAllVisitedViews ({ commit, state }) {
    return new Promise(resolve => {
      commit('DEL_ALL_VISITED_VIEWS')
      resolve([...state.visitedViews])
    })
  },

  // 删除所有组件缓存
  delAllCachedViews ({ commit, state }) {
    return new Promise(resolve => {
      commit('DEL_ALL_CACHED_VIEWS')
      resolve([...state.cachedViews])
    })
  },

  // 更新视图信息
  updateVisitedView ({ commit }, view) {
    commit('UPDATE_VISITED_VIEW', view)
  },

  // 初始化固定标签 - 修复后的版本
  initAffixTags ({ commit, state }) {

    // 定义过滤固定标签的函数（递归处理嵌套路由）
    const filterAffixTags = (routes, basePath = '/') => {
      let tags = []
      routes.forEach(route => {
        // 如果路由标记为固定(affix)，则添加到标签列表
        if (route.meta?.affix) {
          const tagPath = path.resolve(basePath, route.path) // 解析完整路径
          tags.push({
            fullPath: tagPath,
            path: tagPath,
            name: route.name,
            meta: { ...route.meta },
            title: route.meta?.title || route.name || '未命名'
          })
        }

        // 递归处理子路由
        if (route.children) {
          const childBasePath = route.path ? path.resolve(basePath, route.path) : basePath
          const childTags = filterAffixTags(route.children, childBasePath)
          tags = [...tags, ...childTags]
        }
      })
      return tags
    }

    // 获取主应用路由 - 使用导入的 router 实例
    let mainRoutes = []
    try {
      // 从路由配置中获取根路由
      const rootRoute = router.options.routes.find(r => r.path === '/')

      // 获取根路由的子路由作为主路由
      if (rootRoute && rootRoute.children) {
        mainRoutes = rootRoute.children
      } else {
        console.warn('未找到根路由或子路由')
      }
    } catch (e) {
      console.error('获取路由失败:', e)
      // 添加默认首页作为回退方案
      commit('ADD_VISITED_VIEW', {
        path: '/index',
        name: 'index',
        meta: { title: '首页', affix: true }
      })
      return
    }

    // 过滤出所有固定标签
    const affixTags = filterAffixTags(mainRoutes)

    // 添加固定标签到状态中
    affixTags.forEach(tag => {
      // 避免重复添加 - 只检查路径是否相同
      const exists = state.visitedViews.some(v => v.path === tag.path)

      if (!exists) {
        commit('ADD_VISITED_VIEW', tag)
        if (tag.name) {
          commit('ADD_CACHED_VIEW', tag)
        }
      } else {
      }
    })
  }
}

// 导出模块
export default {
  namespaced: true, // 启用命名空间，避免命名冲突
  state,
  mutations,
  actions
}
